/**
 * Button component
 *
 * Optional props:
 * ghost : boolean - ghost style
 * disabled : boolean - disabled style and attributes
 * error : boolean - error style
 * success : boolean - success style
 * secondary : boolean - secondary style
 * inline : boolean - inline style
 * white : boolean - if white look should be applied
 * orange : boolean - if orange look should be applied
 * borderWidth : string - border width (with unit)
 * fontSize : string - font size (with unit)
 * height : string - height (with unit)
 * width : string - width (with unit)
 *
 * Examples for component:
 * <Button inline>Inline button</Button>
 * <Button disabled>Disabled button</Button>
 * <Button ghost success>Text for button</Button>
 *
 */

import { Link } from 'react-router-dom';
import { colors, fonts } from '../../variables';
import styled, { css } from 'styled-components';

const getColor = (props, state = false) => {
	// Disabled
	if (props.disabled) return colors.grey.light;

	// Success
	if (props.success || props.buttonType === 'success') {
		if (state === 'hover') return colors.green.ecoLight;
		if (state === 'active') return colors.green.ecoDark;
		return colors.green.eco;
	}

	// Error
	if (props.error || props.buttonType === 'error') {
		if (state === 'hover') return colors.red.warningLight;
		if (state === 'active') return colors.red.warningDark;
		return colors.red.warning;
	}

	// Secondary
	if (props.secondary || props.buttonType === 'secondary') {
		if (state === 'hover' || state === 'active') return colors.grey.graphite;
		return colors.blue.brommaDarkest;
	}

	// White
	if (props.white || props.buttonType === 'white') {
		if (state === 'hover' || state === 'active') return '#f9f9f9';
		return '#fff';
	}

	if (props.digital || props.buttonType === 'digital') {
		if (state === 'hover' || state === 'active')
			return colors.blue.digitalLight;

		return colors.blue.digital;
	}

	// Orange
	if (props.orange || props.buttonType === 'orange') {
		if (state === 'hover') return colors.orange.spreaderLight;
		if (state === 'active') return colors.orange.spreaderDark;

		return colors.orange.spreader;
	}

	// Default "primary"
	if (state === 'hover') return colors.blue.brommaLight;
	if (state === 'active') return colors.blue.brommaDark;
	return colors.blue.bromma;
};

const getTextColor = props => {
	if (props.inline && props.digital) {
		return colors.blue.digital;
	}

	if (props.secondary) {
		return '#fff';
	}

	return '#000000';
};

const cursor = ({ disabled }) => (disabled ? 'default' : 'pointer');

const baseStyles = css`
	font-family: ${fonts.primary};
	padding: 0.5em 1em;
	cursor: ${props => cursor(props)};
	outline: 0;
	border-width: ${props => props.borderWidth || '2px'};
	border-radius: 4px;
	border-style: solid;
	text-transform: uppercase;
	display: inline-block;
	font-size: ${props => props.fontSize || '14px'};
	border-color: ${props => getColor(props)};
	color: ${props => getTextColor(props)};
	background: ${props => getColor(props)};
	height: ${props => props.height || 'unset'};
	width: ${props => props.width || 'auto'};
	line-height: ${props => props.lineHeight || 'unset'};

	&:hover,
	&:focus {
		background: ${props => getColor(props, 'hover')};
		border-color: ${props => getColor(props, 'hover')};
		opacity: ${props => (props.secondary ? '0.8' : '1')};
	}

	&:active {
		background: ${props => getColor(props, 'active')};
		border-color: ${props => getColor(props, 'active')};
	}

	${props =>
		props.ghost &&
		`
	color: ${getColor(props)};
	background: transparent;

	&:hover, &:focus {
		color: ${getColor(props, 'hover')};
		background: transparent;
	}

	&:active {
		color: ${getColor(props, 'active')}
		background: transparent;
	}
`};

	${props =>
		props.inline &&
		`
	color: ${getColor(props)};
	background: transparent;
	border-color: transparent;
	padding-left: 0;
	padding-right: 0;

	&:hover, &:focus {
		color: ${getColor(props, 'hover')};
		background: transparent;
		border-color: transparent;
	}
`};
`;

export const Button = styled.button`
	${baseStyles};
`;

export const ButtonLink = styled(Link)`
	${baseStyles};
	text-decoration: none;
`;



// WEBPACK FOOTER //
// ./src/components/Common/Button/Button.js